import { useState, useEffect, ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  api,
  getAccessToken,
  getStaffToken,
  setTokens,
  clearTokens,
  clearSelectedContext,
  clearStaffSession,
  persistOrganizationId,
} from "@/lib/api";
import { clearPosSessionStorage } from "@/lib/posSessionStorage";
import { AuthContext } from "@/contexts/auth-context";
import type { AuthContextType, AuthResponse, UserResponseForAuth } from "@/types/auth";

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<UserResponseForAuth | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const queryClient = useQueryClient();

  // Check for existing session on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = getAccessToken();
      if (token) {
        try {
          // Use skipForceRedirect so expired tokens don't hijack public pages
          // (e.g. /feedback/:orderId) with a redirect to login.
          // Include staff token so the API returns org_id/outlet_id when a session exists.
          const userData = await api.get<UserResponseForAuth>("/api/v1/auth/me", {
            skipForceRedirect: true,
          });
          setUser(userData);
          if (userData.org_id) {
            persistOrganizationId(userData.org_id);
          }
        } catch {
          clearTokens();
        }
      }
      setIsLoading(false);
    };

    checkAuth();
  }, []);

  const login = (authResponse: AuthResponse) => {
    // Staff tokens are bound to the tier-1 user session that created them.
    // Clear any existing staff context before replacing the tier-1 session.
    clearStaffSession();
    setTokens(authResponse.access_token, authResponse.refresh_token);
    setUser(authResponse.user);
    // Persist org ID from user object or fall back to available_outlets
    const orgId = authResponse.user.org_id
      ?? authResponse.available_outlets?.[0]?.organization_id;
    if (orgId) {
      persistOrganizationId(orgId);
    }
  };

  /**
   * Signs out of this device: ends the staff session and the login on the server, then
   * forgets every token, the selected outlet or brand, the POS "where was I" state and
   * all cached queries. Callers then open the login screen (useSignOut does both).
   */
  const logout = async () => {
    try {
      // First end the staff session, while its token is still stored. A selection left
      // half-finished (PIN entered, terminal not picked yet) is ended here too.
      if (getStaffToken()) {
        try {
          await api.post("/api/v1/auth/staff/logout", undefined, { skipForceRedirect: true });
        } catch {
          // Ignore staff logout errors
        }
      }
      // The user login is NOT ended on the server: /auth/logout signs the account out on
      // every device, and restaurants share one login across their terminals. Signing out
      // here only forgets this device's tokens (below).
    } catch {
      // Ignore logout errors: the device signs out whatever the server says.
    } finally {
      clearTokens(); // Also clears the staff session, brand session and selected context
      clearSelectedContext();
      clearPosSessionStorage();
      // The next person to sign in must not see this account's outlets, staff or data.
      queryClient.clear();
      setUser(null);
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    setUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
